import React, { memo, useEffect, useMemo } from 'react';
import { IEmitterReactProps, ITuple3 } from '../../../component-data-structure';
import { Emitter } from '../../../threejs-particle-system/src/v1/core/Emitter';
import EmitterGroupWrapper from '../r3f-wrapper/EmitterGroupWrapper';
import { convertDocToThreeEmitterGroupSettings, convertDocToThreeEmitterSettings } from '../utils';

const BasicSingleEmitter: React.FunctionComponent<IEmitterReactProps> = ({ 
    id,
    emitter: emSettings, // Constructor props
    emitterGroup: emGroupSettings, // Constructor props
    isPlaying,
    duration,
    runCounter,
    isStatic,
    scale = [1, 1, 1],
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    renderOrder,
    onFinish
}) => {

    // Here we are constructing the emitter and emitterGroup based on their settings.
    //
    // This used to be `useMemo(..., [])` — built once, on the grounds that construction is expensive and
    // only runtime props (duration, isPlaying, autoplay) ever needed to change. That held while a content
    // doc could not change after boot; `Jute.applyContentUpdate` broke it, and the result was that NO
    // emitter edit was ever visible, however far up the tree the new settings were made reactive.
    // (`OcclusionCoreAdditiveSingleEmitter` already deps on `[emitterSettings]` correctly — it feeds this
    // component, which then re-froze them.) Reactivity now wins; the perf argument is preserved by the
    // key below rather than by never recomputing.
    //
    // Keyed on the SERIALIZED settings, not the object references. The reconcile in JUTE
    // (`content-reconcile.ts`) replaces the whole entity object when any part of it changes, so
    // `emSettings` / `emGroupSettings` get fresh references when an emitter is merely dragged or its
    // render order changes. Depending on the references directly would therefore rebuild the entire
    // particle system — and reload its texture, see `convertDocToThreeEmitterGroupSettings` — on every
    // frame of a drag. Stringifying costs nothing next to that, and the conversion below already does a
    // JSON round-trip anyway. See PROJECT_JUTE_PREVIEW.md in the Designer repo.
    // DO NOT SPLIT THIS INTO TWO MEMOS. Returning both settings objects from one memo is load-bearing:
    // it guarantees that a new `emitterGroupSettings` always arrives with a new `emitterSettings`, and so
    // with a brand new `Emitter` below. `EmitterGroup.addEmitter` sets `emitter.group = this` and
    // `removeEmitter` never nulls it back, so an Emitter is latched to the first group it ever joins —
    // re-adding one to a rebuilt group hits the `emitter.group !== null` guard, which logs
    // 'Emitter already belongs to another group' and returns WITHOUT adding. Splitting these so that
    // group settings could recompute alone is the obvious optimisation and would silently kill the
    // emitter whenever only its group settings were edited.
    const emitterKey = JSON.stringify(emSettings);
    const emitterGroupKey = JSON.stringify(emGroupSettings);
    const { emitterGroupSettings, emitterSettings } = useMemo(() => {
        const emitterSettings = convertDocToThreeEmitterSettings(emSettings);
        const emitterGroupSettings = convertDocToThreeEmitterGroupSettings(emGroupSettings);
        return {emitterSettings, emitterGroupSettings}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emitterKey, emitterGroupKey]);

    let emitter: Emitter | undefined = useMemo(() => new Emitter(emitterSettings), [emitterSettings])

    // Make sure GC frees emitter memory on unmount
    useEffect(() => {
        return () => { emitter = undefined }
    }, [])

    // Call onFinish once emitter has finished playing
    useEffect(() => {
        if (!emitter) return;
        emitter.particlesPerSecond = emitter.particleCount / emitter.maxAge.value;
        emitter.onFinish(onFinish);
    }, [emitter, onFinish])

    //// Start runtime prop logic ////
    useEffect(() => {
        if (typeof isStatic === 'undefined' || typeof emitter === 'undefined') return;
        emitter.isStatic = isStatic;
    })

   useEffect(() => {
        if (typeof duration === 'undefined' || typeof emitter === 'undefined') return;
        emitter.duration = duration;
    }, [duration, emitter, runCounter])

    // Enable and disable emitter on play state changes
    useEffect(() => {
        if (!emitter) return;
        if (isPlaying) {
            emitter?.reset(true)
            emitter?.enable();
            return;
        } 
        emitter.reset();
    }, [isPlaying, emitter, runCounter])

    return (
        <EmitterGroupWrapper 
            args={[emitterGroupSettings]} 
            id={id}
            position={position as ITuple3}
            rotation={rotation as ITuple3}
            scale={scale as ITuple3}
            renderOrder={renderOrder}
        >
            {emitter}
        </EmitterGroupWrapper>
    );
}
export default memo(BasicSingleEmitter);

